import React, { useEffect, useRef } from 'react';

import { useLocation } from 'react-router-dom';

import { MenuObjectTypes } from 'enums/menu';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { IMParticleCtx, useMParticleContext } from 'state/mParticle';
import { ClickEventComponentNames, CustomEventNames, EventTypes } from 'state/mParticle/constants';
import { useMainMenuContext, useNewMainMenuContext } from 'state/main-menu';
import { useMenuContext } from 'state/menu';
import { isExternalLink } from 'utils/is-external-link';
import { optionToUrl } from 'utils/menu';
import { useIsBrowseMenu } from 'utils/menu/hooks/use-is-browse-menu';
import { routes } from 'utils/routing';

import { MenuListGridInner, MenuTileGridInner } from '../menu-tile-grid-container';
import Tile from '../tile';
import { TileLink, TileLinkExternal, TileListLink, TileListLinkExternal } from '../tile-link';

import {
  emitTileClickedEvent,
  getCaloriesAndPrice,
  getDescription,
  getImageFromOption,
} from './helpers';
import { useCalories } from './hooks';
import { IMenuTile } from './types';

const SECTION_TILE_STRING = 'section';
export const logClickEvent = (
  tileType: string,
  productId: string,
  productName: string,
  mParticle: IMParticleCtx
) => {
  if (tileType === SECTION_TILE_STRING) {
    mParticle.logRBIEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Navigation,
      attributes: {
        component: ClickEventComponentNames.PRODUCT_SECTION_TILES,
        text: productName,
        componentId: productId,
      },
    });
  } else {
    mParticle.logRBIEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Navigation,
      attributes: {
        component: ClickEventComponentNames.PRODUCTS,
        text: productName,
        componentId: productId,
      },
    });
  }
};

const NewMenuTile = ({ enableMenuList, menuData, objectFitContain = false }: IMenuTile) => {
  const firstMenuItemRef = useRef<HTMLAnchorElement | null>(null);
  const { pathname } = useLocation();
  const mParticle = useMParticleContext();

  const { isBrowseMenu } = useIsBrowseMenu(pathname);
  const getCalories = useCalories();
  const { checkIfUserIsOnMainMenuPath, getPricingAndAvailability, isStaticMenu } =
    useNewMainMenuContext();

  const MenuTileContainer = enableMenuList ? MenuListGridInner : MenuTileGridInner;
  const serviceDataEnabled = useFlag(LaunchDarklyFlag.ENABLE_MENU_SERVICE_DATA);
  const Link = enableMenuList ? TileListLink : TileLink;
  const LinkExternal = enableMenuList ? TileListLinkExternal : TileLinkExternal;

  const filteredMenuData = !isStaticMenu
    ? menuData.filter(option => getPricingAndAvailability(option._id)?.isAvailable)
    : menuData;

  useEffect(() => {
    // only pull focus to the first item if we are not on the main menu
    if (pathname !== routes.menu) {
      firstMenuItemRef.current?.focus();
    }
  }, [pathname]);

  return (
    <MenuTileContainer $isOnMainMenu={checkIfUserIsOnMainMenuPath()}>
      {filteredMenuData.map((option, menuItemIndex) => {
        const hideCalories = !!option.hideCalories;

        const { calories, price } = getCaloriesAndPrice(option, getCalories, undefined, true); // we always enable the menu service data here because this is the "new" menu-tile

        const tile = (
          <Tile
            hideCalories={hideCalories}
            calories={calories}
            description={getDescription(option)}
            enableMenuList={enableMenuList}
            id={option._id}
            image={getImageFromOption(option)}
            imageDescription={option.imageDescription?.locale}
            name={option.name?.locale}
            objectFitContain={objectFitContain}
            price={price}
            type={option._type}
          />
        );

        const redirectUrl =
          option._type === MenuObjectTypes.SECTION && option?.redirectLink?.locale;

        if (redirectUrl && isExternalLink(redirectUrl)) {
          return (
            <LinkExternal key={option._id} href={redirectUrl}>
              {tile}
            </LinkExternal>
          );
        }

        const handleLinkClick = () => {
          emitTileClickedEvent({
            menuServiceDataEnabled: serviceDataEnabled,
            pathname,
            sanityId: option._id,
          });
          logClickEvent(option._type, option._id, option.name.locale, mParticle);
        };

        return (
          <Link
            ref={menuItemIndex === 0 ? firstMenuItemRef : undefined}
            $transparent={option._type !== MenuObjectTypes.SECTION}
            key={option._id}
            aria-labelledby={option._id}
            onClick={handleLinkClick}
            to={redirectUrl || optionToUrl(option, isBrowseMenu)}
          >
            {tile}
          </Link>
        );
      })}
    </MenuTileContainer>
  );
};

const LegacyMenuTile = ({ enableMenuList, menuData, objectFitContain = false }: IMenuTile) => {
  const firstMenuItemRef = useRef<HTMLAnchorElement | null>(null);
  const { pathname } = useLocation();
  const mParticle = useMParticleContext();

  const { isBrowseMenu } = useIsBrowseMenu(pathname);
  const getCalories = useCalories();
  const { checkIfUserIsOnMainMenuPath } = useMainMenuContext();
  const menuCtx = useMenuContext();
  const menuServiceDataEnabled = useFlag(LaunchDarklyFlag.ENABLE_MENU_SERVICE_DATA);

  const MenuTileContainer = enableMenuList ? MenuListGridInner : MenuTileGridInner;

  const Link = enableMenuList ? TileListLink : TileLink;
  const LinkExternal = enableMenuList ? TileListLinkExternal : TileLinkExternal;

  useEffect(() => {
    // only pull focus to the first item if we are not on the main menu
    if (pathname !== routes.menu) {
      firstMenuItemRef.current?.focus();
    }
  }, [pathname]);

  return (
    <MenuTileContainer $isOnMainMenu={checkIfUserIsOnMainMenuPath()}>
      {menuData.map((option, menuItemIndex) => {
        const hideCalories = !!option.hideCalories;
        const { calories, price } = getCaloriesAndPrice(option, getCalories);

        const tile = (
          <Tile
            hideCalories={hideCalories}
            calories={calories}
            description={getDescription(option)}
            enableMenuList={enableMenuList}
            id={option._id}
            image={getImageFromOption(option)}
            imageDescription={option.imageDescription?.locale}
            name={option.name.locale}
            objectFitContain={objectFitContain}
            price={price}
            type={option._type}
          />
        );

        const redirectUrl =
          option._type === MenuObjectTypes.SECTION && option?.redirectLink?.locale;

        if (redirectUrl && isExternalLink(redirectUrl)) {
          return (
            <LinkExternal key={option._id} href={redirectUrl}>
              {tile}
            </LinkExternal>
          );
        }

        const handleLinkClick = () => {
          emitTileClickedEvent({
            sanityId: option._id,
            pathname,
            menuServiceDataEnabled,
          });
          logClickEvent(option._type, option._id, option.name.locale, mParticle);
        };

        return (
          <Link
            ref={menuItemIndex === 0 ? firstMenuItemRef : undefined}
            $transparent={option._type !== MenuObjectTypes.SECTION}
            key={option._id}
            to={redirectUrl || optionToUrl(option, isBrowseMenu)}
            onClick={handleLinkClick}
            onMouseEnter={() => {
              menuCtx.getMenuObject(`${option._type}-${option._id}`);
            }}
          >
            {tile}
          </Link>
        );
      })}
    </MenuTileContainer>
  );
};

const MenuTile = ({
  enableMenuServiceData,
  enableMenuList,
  menuData,
  objectFitContain = false,
}: IMenuTile) => {
  if (enableMenuServiceData) {
    return (
      <NewMenuTile
        enableMenuList={enableMenuList}
        menuData={menuData}
        objectFitContain={objectFitContain}
      />
    );
  }

  return (
    <LegacyMenuTile
      enableMenuList={enableMenuList}
      menuData={menuData}
      objectFitContain={objectFitContain}
    />
  );
};

export default MenuTile;
